import { inject } from "mobx-react";
import { observer } from "mobx-react-lite";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { Route, Switch, useHistory, useRouteMatch } from "react-router";
import {
  CreateView,
  IndexView,
  SecuredButton,
  SecurityProvider,
  ShowView,
  withBreadCrumbs
} from "lego-gui-lib/lib";
import { useFirstValue } from "../../../common/common";
import {
  TabContentContainer,
  TabTitleContainer,
  TabViewContainer
} from "../../../common/tabContainers";
import {
  treeContext,
  TreeCreateSchema,
  TreeSchema,
  TreeSchemaColumns,
  TreeShowSchema,
  TreeStatus,
  TreeType
} from "../schema";
import { TreeTabsView } from "./TreeTabsView";
import { ParentRoute } from "../../../core/model/parent";

export const TreeViewComponent = () => {
  const match = useRouteMatch();
  const baseUrl = match.path;
  return (
    <>
      <ParentRoute path={baseUrl + "/:id"} dataSourceName={"tree"} />
      <Switch>
        <Route exact={true} path={baseUrl} component={TreeIndex} />
        <Route path={baseUrl + "/create/:id"} component={Clone} />
        <Route path={baseUrl + "/create"} component={Create} />
        <Route path={baseUrl + "/:id"} component={TreeTabsView} />
      </Switch>
    </>
  );
};

const injectDataSource = (comp: any) =>
  inject("SAN", "tree", "i18n", "commons")(observer(comp));

export const TreeIndex = injectDataSource(({ tree, i18n, ...rest }: any) => {
  const title = i18n.t("hns:tree:domain", { count: 2 });
  return (
    <IndexView
      dataSource={tree.listDataSource}
      {...rest}
      title={title}
      titleVariant={"h5"}
      toolbarButtons={{ delete: true, export: true }}
      rowButtons={{ edit: true, delete: true, copy: true, export: true }}
      columns={TreeSchemaColumns}
      titleContainer={TabTitleContainer}
      viewContainer={TabViewContainer}
      contentContainer={TabContentContainer}
    />
  );
});

export const Create = injectDataSource(
  ({ SAN, tree, i18n, match, ...rest }: any) => {
    const title = i18n.t("common:create", { domain: "hns:tree:domain" });
    const baseUrl = match.url;
    const history = useHistory();
    return withBreadCrumbs(
      { to: baseUrl + "/create", children: title },
      <CreateView
        dataSource={tree.oneDataSource}
        {...rest}
        title={title}
        fields={TreeCreateSchema}
        id={match.params}
        goBack={() => history.goBack()}
      />
    );
  }
);

export const Clone = observer(({ ...rest }: any) => {
  const match = useRouteMatch();
  const tree = useContext(treeContext);
  const i18n = useTranslation();
  const name = useFirstValue(tree.oneDataSource.one, ds => ds.name);
  const title = i18n.t("common:copyTitle", {
    domain: "hns:tree:domain",
    id: name
  });
  const history = useHistory();
  return withBreadCrumbs(
    { to: match.url + "/copy", children: i18n.t("common:copy") },
    <CreateView
      submit={async () => {
        return tree.oneDataSource.clone();
      }}
      dataSource={tree.oneDataSource}
      {...rest}
      title={title}
      fields={TreeCreateSchema}
      id={match.params}
      goBack={() => history.goBack()}
    />
  );
});

interface ActivationButtonsProps {
  tree: any;
  activateTree: () => void;
  deactivateTree: () => void;
}

const ActivationButtons = observer((props: ActivationButtonsProps) => {
  const i18n = useTranslation();
  const tree = props.tree;
  const activateTree = () => {
    props.activateTree();
  };

  return tree?.status == TreeStatus.INACTIVE &&
    tree?.type == TreeType.MAIN_TREE ? (
    <span>
      <b>{i18n.t("common:action_plural")}</b> <br />
      <SecuredButton
        requiredAccess={"editable"}
        variant="outlined"
        aria-label="Activate"
        color="primary"
        onClick={activateTree}
      >
        {i18n.t("hns:tree:activate")}
      </SecuredButton>
    </span>
  ) : (
    <></>
  );
});

export const Show = observer(({ id }: any) => {
  const tree = useContext(treeContext);

  function useForceUpdate() {
    const [, setValue] = useState(0);
    return () => setValue(value => ++value);
  }

  const history = useHistory();

  const forceUpdate = useForceUpdate();

  const activateTree = async () => {
    await tree.oneDataSource.activateTree();
    await tree.oneDataSource.findOne(id);
    forceUpdate();
  };

  const deactivateTree = async () => {
    await tree.oneDataSource.deactivateTree();
    await tree.oneDataSource.findOne(id);
    forceUpdate();
  };

  return (
    <SecurityProvider security={TreeSchema.security}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(auto, 800px) 150px",
          columnGap: "15px"
        }}
      >
        <ShowView
          dataSource={tree.oneDataSource}
          prefix={false}
          hideDeleteButton={true}
          preloadData={false}
          titleRow={false}
          fields={TreeShowSchema}
          id={id}
          goBack={() => history.goBack()}
        />
        <ActivationButtons
          tree={tree.oneDataSource.one}
          activateTree={activateTree}
          deactivateTree={deactivateTree}
        />
      </div>
    </SecurityProvider>
  );
});
